import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Camera,
  CanvasTexture,
  Group,
  Object3D,
  Points,
  Scene,
  ShaderMaterial,
  Sprite,
  SpriteMaterial,
  Vector3,
} from "three"
import { GLBModel } from "./GLBModel"

export type TubeWorldParticleStreamOptions = {
  maxParticles?: number
  spawnPerSecond?: number
  speedMin?: number
  speedMax?: number
  lifetimeMin?: number
  lifetimeMax?: number
  positionJitter?: number
  /** Полуапертурный угол конуса вокруг направления камеры (рад.) */
  directionConeHalfAngle?: number
  pointSize?: number
  formulaSpawnPerSecond?: number
}

export type TubeWorldParticleStream = {
  update: (sourceMesh: Object3D, camera: Camera, emitting?: boolean) => void
  dispose: () => void
}

const tmpDir = new Vector3()
const tmpU = new Vector3()
const tmpV = new Vector3()
const tmpVel = new Vector3()
const tmpSpawn = new Vector3()
const tmpForward = new Vector3()
export const SORBENT_FORMULA_LABELS = ["C", "SiO2"] as const
const formulaColors = ["#f4fbff", "#d7f4ff"]

function createFormulaTexture(text: string, color: string): CanvasTexture {
  const canvas = document.createElement("canvas")
  const scale = Math.min(window.devicePixelRatio || 1, 2)
  canvas.width = 256 * scale
  canvas.height = 96 * scale

  const context = canvas.getContext("2d")
  if (context) {
    context.scale(scale, scale)
    context.clearRect(0, 0, 256, 96)
    context.font = "800 42px Arial, sans-serif"
    context.textAlign = "center"
    context.textBaseline = "middle"
    context.shadowColor = "rgba(14, 54, 74, 0.75)"
    context.shadowBlur = 13
    context.lineWidth = 7
    context.strokeStyle = "rgba(3, 24, 34, 0.7)"
    context.strokeText(text, 128, 48)
    context.fillStyle = color
    context.fillText(text, 128, 48)
  }

  const texture = new CanvasTexture(canvas)
  texture.needsUpdate = true

  return texture
}

/** Равномерная случайная единичная вектор внутри конуса вокруг `axis` (полуапертур `halfAngleRad`). */
function randomUnitInCone(axis: Vector3, halfAngleRad: number, out: Vector3): void {
  if (halfAngleRad < 1e-6) {
    out.copy(axis)
    return
  }
  if (Math.abs(axis.x) < 0.9) {
    tmpU.set(1, 0, 0)
  } else {
    tmpU.set(0, 1, 0)
  }
  tmpU.crossVectors(axis, tmpU)
  if (tmpU.lengthSq() < 1e-10) {
    tmpU.set(0, 0, 1).crossVectors(axis, tmpU)
  }
  tmpU.normalize()
  tmpV.crossVectors(axis, tmpU).normalize()

  const cosLimit = Math.cos(halfAngleRad)
  const cosTheta = 1 - Math.random() * (1 - cosLimit)
  const sinTheta = Math.sqrt(Math.max(0, 1 - cosTheta * cosTheta))
  const phi = Math.random() * Math.PI * 2

  out
    .copy(axis)
    .multiplyScalar(cosTheta)
    .addScaledVector(tmpU, sinTheta * Math.cos(phi))
    .addScaledVector(tmpV, sinTheta * Math.sin(phi))
    .normalize()
}

/**
 * Поток частиц в мировых координатах: скорость фиксируется при рождении по направлению камеры,
 * дальше не зависит от поворота камеры. Точка `Points` в корне сцены с единичным transform.
 */
export function createTubeWorldParticleStream(
  scene: Scene | Group,
  options: TubeWorldParticleStreamOptions = {},
): TubeWorldParticleStream {
  const maxParticles = options.maxParticles ?? 20000
  const spawnPerSecond = options.spawnPerSecond ?? 1716
  const speedMin = options.speedMin ?? 1.55
  const speedMax = options.speedMax ?? 2.65
  const lifetimeMin = options.lifetimeMin ?? 0.62
  const lifetimeMax = options.lifetimeMax ?? 1.38
  const positionJitter = options.positionJitter ?? 0.056
  const directionConeHalfAngle = options.directionConeHalfAngle ?? 0.18
  const pointSize = options.pointSize ?? 0.0768
  const maxFormulaSprites = 28
  const formulaSpawnPerSecond = options.formulaSpawnPerSecond ?? 4

  const positions = new Float32Array(maxParticles * 3)
  const lives = new Float32Array(maxParticles)
  const velocities = new Float32Array(maxParticles * 3)
  const timeLeft = new Float32Array(maxParticles)
  const maxLife = new Float32Array(maxParticles)
  const alive = new Uint8Array(maxParticles)
  const particleSizes = new Float32Array(maxParticles)
  const particleTones = new Float32Array(maxParticles)
  const formulaVelocities = new Float32Array(maxFormulaSprites * 3)
  const formulaPositions = new Float32Array(maxFormulaSprites * 3)
  const formulaTimeLeft = new Float32Array(maxFormulaSprites)
  const formulaMaxLife = new Float32Array(maxFormulaSprites)
  const formulaSizes = new Float32Array(maxFormulaSprites)
  const formulaAlive = new Uint8Array(maxFormulaSprites)
  const formulaTextures = SORBENT_FORMULA_LABELS.map((label, index) =>
    createFormulaTexture(label, formulaColors[index % formulaColors.length])
  )

  const geometry = new BufferGeometry()
  geometry.setAttribute("position", new BufferAttribute(positions, 3))
  geometry.setAttribute("life", new BufferAttribute(lives, 1))
  geometry.setAttribute("particleSize", new BufferAttribute(particleSizes, 1))
  geometry.setAttribute("particleTone", new BufferAttribute(particleTones, 1))

  const material = new ShaderMaterial({
    uniforms: {
      uSize: { value: pointSize },
    },
    vertexShader: `
      uniform float uSize;
      attribute float life;
      attribute float particleSize;
      attribute float particleTone;
      varying float vLife;
      varying float vTone;

      void main() {
        vLife = life;
        vTone = particleTone;
        if (life <= 0.001) {
          gl_PointSize = 0.0;
          gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
          return;
        }
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_PointSize = uSize * particleSize * (300.0 / max(-mvPosition.z, 0.001));
        gl_Position = projectionMatrix * mvPosition;
      }
    `,
    fragmentShader: `
      varying float vLife;
      varying float vTone;

      float hash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
      }

      void main() {
        vec2 p = gl_PointCoord - vec2(0.5);
        float edge = 0.45;
        float r = length(p);
        if (r > edge) discard;

        float grain = hash(floor(gl_PointCoord * 8.0) + vTone * 29.0);
        float core = 1.0 - smoothstep(0.0, edge, r);
        vec3 warmWhite = mix(vec3(0.83, 0.86, 0.78), vec3(1.0, 0.99, 0.94), vTone);
        vec3 color = warmWhite * (0.88 + core * 0.18 + grain * 0.08);
        gl_FragColor = vec4(color, 1.0);
      }
    `,
    transparent: false,
    depthWrite: true,
    depthTest: true,
  })

  const points = new Points(geometry, material)
  points.frustumCulled = false
  points.matrixAutoUpdate = false
  points.renderOrder = 1_000_000;
  points.updateMatrix()
  points.layers.set(1);
  scene.add(points)

  const formulaSprites = Array.from({ length: maxFormulaSprites }, (_, index) => {
    const sprite = new Sprite(
      new SpriteMaterial({
        map: formulaTextures[index % formulaTextures.length],
        transparent: true,
        opacity: 0,
        depthWrite: false,
        depthTest: true,
        blending: AdditiveBlending,
      })
    )
    sprite.frustumCulled = false
    sprite.visible = false
    sprite.renderOrder = 1_000_001
    scene.add(sprite)

    return sprite
  })

  let spawnCarry = 0
  let formulaSpawnCarry = 0
  let lastNow = performance.now() / 1000
  let aliveCount = 0
  let maxUsedIndex = -1

  // Free-list: стек свободных индексов, заполняем в обратном порядке чтобы спавнить с начала
  const freeList = new Int32Array(maxParticles)
  let freeCount = maxParticles
  for (let i = 0; i < maxParticles; i++) freeList[i] = maxParticles - 1 - i

  const spawnOne = (sourceMesh: Object3D, camera: Camera) => {
    if (freeCount === 0) return
    const idx = freeList[--freeCount]
    if (idx > maxUsedIndex) maxUsedIndex = idx

    sourceMesh.getWorldPosition(tmpSpawn)

    camera.getWorldDirection(tmpForward)
    tmpSpawn.addScaledVector(tmpForward, 0.5)

    const plumeLane = Math.random()
    const j = positionJitter * (plumeLane < 0.66 ? 0.58 : 1.15)
    tmpSpawn.x += (Math.random() * 2 - 1) * j
    tmpSpawn.y += (Math.random() * 2 - 1) * j
    tmpSpawn.z += (Math.random() * 2 - 1) * j

    const coneScale = plumeLane < 0.62 ? 0.36 : plumeLane < 0.9 ? 0.66 : 0.95
    randomUnitInCone(tmpForward, directionConeHalfAngle * coneScale, tmpVel)
    const speed = speedMin + Math.random() * (speedMax - speedMin)
    tmpVel.multiplyScalar(speed * (plumeLane < 0.9 ? 1 : 0.72))

    const i3 = idx * 3
    positions[i3] = tmpSpawn.x
    positions[i3 + 1] = tmpSpawn.y
    positions[i3 + 2] = tmpSpawn.z

    velocities[i3] = tmpVel.x
    velocities[i3 + 1] = tmpVel.y
    velocities[i3 + 2] = tmpVel.z

    const T =
      (lifetimeMin + Math.random() * (lifetimeMax - lifetimeMin)) *
      (plumeLane < 0.9 ? 1 : 0.84)
    timeLeft[idx] = T
    maxLife[idx] = T
    lives[idx] = 1
    alive[idx] = 1
    aliveCount++
    particleSizes[idx] =
      plumeLane < 0.58
        ? 0.46 + Math.random() * 0.32
        : plumeLane < 0.88
          ? 0.68 + Math.random() * 0.34
          : 0.95 + Math.random() * 0.42
    particleTones[idx] = Math.random()
  }

  const spawnFormula = (sourceMesh: Object3D, camera: Camera) => {
    let idx = -1
    for (let i = 0; i < maxFormulaSprites; i++) {
      if (!formulaAlive[i]) {
        idx = i
        break
      }
    }
    if (idx < 0) {
      return
    }

    sourceMesh.getWorldPosition(tmpSpawn)

    camera.getWorldDirection(tmpForward)
    tmpSpawn.addScaledVector(tmpForward, 0.5)

    const j = positionJitter * 2.2
    tmpSpawn.x += (Math.random() * 2 - 1) * j
    tmpSpawn.y += (Math.random() * 2 - 1) * j
    tmpSpawn.z += (Math.random() * 2 - 1) * j

    randomUnitInCone(tmpForward, directionConeHalfAngle * 0.75, tmpVel)
    tmpVel.multiplyScalar(2.4 + Math.random() * 1.35)

    const i3 = idx * 3
    formulaPositions[i3] = tmpSpawn.x
    formulaPositions[i3 + 1] = tmpSpawn.y
    formulaPositions[i3 + 2] = tmpSpawn.z
    formulaVelocities[i3] = tmpVel.x
    formulaVelocities[i3 + 1] = tmpVel.y
    formulaVelocities[i3 + 2] = tmpVel.z

    const maxT = 0.9 + Math.random() * 0.6
    formulaTimeLeft[idx] = maxT
    formulaMaxLife[idx] = maxT
    formulaSizes[idx] = 0.15 + Math.random() * 0.05
    formulaAlive[idx] = 1

    const sprite = formulaSprites[idx]
    sprite.material.map = formulaTextures[Math.floor(Math.random() * formulaTextures.length)]
    sprite.material.opacity = 0
    sprite.material.needsUpdate = true
    sprite.visible = true
  }

  return {
    update(sourceMesh: Object3D, camera: Camera, emitting = true) {
      const now = performance.now() / 1000
      const dt = Math.min(0.08, Math.max(0, now - lastNow))
      lastNow = now

      const loopEnd = maxUsedIndex + 1
      for (let i = 0; i < loopEnd; i++) {
        if (!alive[i]) {
          continue
        }
        const i3 = i * 3
        const t = timeLeft[i] - dt
        if (t <= 0) {
          alive[i] = 0
          aliveCount--
          freeList[freeCount++] = i
          if (i === maxUsedIndex) {
            while (maxUsedIndex >= 0 && !alive[maxUsedIndex]) maxUsedIndex--
          }
          lives[i] = 0
          positions[i3] = 0
          positions[i3 + 1] = 0
          positions[i3 + 2] = 0
          continue
        }
        timeLeft[i] = t
        lives[i] = maxLife[i] > 1e-6 ? t / maxLife[i] : 0

        const turbulence = (0.035 + (1 - lives[i]) * 0.16) * dt
        velocities[i3] += (Math.random() * 2 - 1) * turbulence
        velocities[i3 + 1] += (Math.random() * 2 - 1) * turbulence
        velocities[i3 + 2] += (Math.random() * 2 - 1) * turbulence

        positions[i3] += velocities[i3] * dt
        positions[i3 + 1] += velocities[i3 + 1] * dt
        positions[i3 + 2] += velocities[i3 + 2] * dt
      }

      for (let i = 0; i < maxFormulaSprites; i++) {
        if (!formulaAlive[i]) {
          continue
        }

        const i3 = i * 3
        const t = formulaTimeLeft[i] - dt
        const sprite = formulaSprites[i]

        if (t <= 0) {
          formulaAlive[i] = 0
          formulaTimeLeft[i] = 0
          sprite.visible = false
          sprite.material.opacity = 0
          continue
        }

        formulaTimeLeft[i] = t
        formulaPositions[i3] += formulaVelocities[i3] * dt
        formulaPositions[i3 + 1] += formulaVelocities[i3 + 1] * dt
        formulaPositions[i3 + 2] += formulaVelocities[i3 + 2] * dt

        const life = formulaMaxLife[i] > 1e-6 ? t / formulaMaxLife[i] : 0
        const fadeIn = Math.min(1, (formulaMaxLife[i] - t) * 8)
        const size = formulaSizes[i] * (0.92 + (1 - life) * 0.24)

        sprite.position.set(
          formulaPositions[i3],
          formulaPositions[i3 + 1],
          formulaPositions[i3 + 2]
        )
        sprite.scale.set(size * 2.85, size * 1.1, 1)
        sprite.material.opacity = life * fadeIn * 0.76
      }

      if (emitting) {
        const emissionPulse =
          0.7 +
          0.18 * Math.sin(now * 5.4) +
          0.16 * Math.sin(now * 12.1 + 1.8)
        spawnCarry += spawnPerSecond * Math.max(0.46, emissionPulse) * dt
        let toSpawn = Math.floor(spawnCarry)
        spawnCarry -= toSpawn
        toSpawn = Math.min(toSpawn, 32)
        for (let s = 0; s < toSpawn; s++) {
          spawnOne(sourceMesh, camera)
        }

        formulaSpawnCarry += formulaSpawnPerSecond * dt
        let formulasToSpawn = Math.floor(formulaSpawnCarry)
        formulaSpawnCarry -= formulasToSpawn
        formulasToSpawn = Math.min(formulasToSpawn, 2)
        for (let s = 0; s < formulasToSpawn; s++) {
          spawnFormula(sourceMesh, camera)
        }
      } else {
        spawnCarry = 0
        formulaSpawnCarry = 0
      }

      if (aliveCount > 0 || emitting) {
        geometry.attributes.position.needsUpdate = true
        const lifeAttr = geometry.attributes.life as BufferAttribute
        lifeAttr.needsUpdate = true
        const sizeAttr = geometry.attributes.particleSize as BufferAttribute
        const toneAttr = geometry.attributes.particleTone as BufferAttribute
        sizeAttr.needsUpdate = true
        toneAttr.needsUpdate = true
      }
    },

    dispose() {
      scene.remove(points)
      for (const sprite of formulaSprites) {
        scene.remove(sprite)
        sprite.material.dispose()
      }
      for (const texture of formulaTextures) {
        texture.dispose()
      }
      geometry.dispose()
      material.dispose()
    },
  }
}

export class TubeModel extends GLBModel {
  constructor() {
    super("/models/weapon/tube.glb")
  }

  get key(): string {
    return "weapon_tube"
  }

  /** Поток частиц в мировом пространстве (см. `createTubeWorldParticleStream`). */
  createWorldParticleStream(
    scene: Scene | Group,
    options?: TubeWorldParticleStreamOptions,
  ): TubeWorldParticleStream {
    return createTubeWorldParticleStream(scene, options)
  }

  getModel(): Object3D | null {
    const mesh = super.getModel()

    if (!mesh) {
      return null
    }

    mesh.traverse((child) => {
      child.layers.set(1);
    });

    return mesh
  }
}
